import { useMemo } from 'react';
import type { Subscription } from 'shared/services/user-subscription';

export interface UseSubscriptionCancelOptionsParams {
  status: Subscription['status'];
  branch: string;
  hasTrial: boolean;
  providerProcessor: string;
  isInsurance: boolean;
}

export interface SubscriptionCancelOptions {
  labelCancelWithRefund: string | undefined;
  labelCancelWithoutRefund: string | undefined;
  showCancelActions: boolean;
}

/**
 * Determina las opciones de cancelación a mostrar en una suscripción.
 * Para suscripciones de tipo seguro (isInsurance, ej. Soda Seguros) no se muestran
 * "Cancelar con reembolso" ni "Cancelar sin reembolso".
 */
export function useSubscriptionCancelOptions({
  status,
  branch,
  hasTrial,
  providerProcessor,
  isInsurance,
}: UseSubscriptionCancelOptionsParams): SubscriptionCancelOptions {
  return useMemo(() => {
    if (isInsurance) {
      return {
        labelCancelWithRefund: undefined,
        labelCancelWithoutRefund: undefined,
        showCancelActions: false,
      };
    }

    const statusLower = status.toLowerCase();
    const showCancelActions =
      statusLower === 'successful' || statusLower === 'pending';

    const labelCancelWithRefund =
      statusLower === 'successful' &&
      branch?.toLowerCase() === 'pr' &&
      !hasTrial &&
      providerProcessor?.toLowerCase() === 'stripe'
        ? 'Cancelar con reembolso'
        : undefined;

    const labelCancelWithoutRefund = showCancelActions
      ? 'Cancelar sin reembolso'
      : undefined;

    return {
      labelCancelWithRefund,
      labelCancelWithoutRefund,
      showCancelActions,
    };
  }, [status, branch, hasTrial, providerProcessor, isInsurance]);
}
